/**
 * Cognito Authentication Service for Rider Gateway (Mobile)
 * Mobile-optimized OAuth 2.0 PKCE implementation with offline support
 */

import { authConfig, authEndpoints, AuthError, mobileAuthSettings } from './authConfig';
import type { AuthTokens, JWTUser } from './authConfig';
import { TokenManager } from './tokenManager';

export interface AuthService {
  login(): Promise<void>;
  handleCallback(urlParams: URLSearchParams): Promise<JWTUser>;
  logout(): Promise<void>;
  getCurrentUser(): Promise<JWTUser | null>;
  refreshTokens(): Promise<AuthTokens>;
  isAuthenticated(): boolean;
  isOffline(): boolean;
}

export class CognitoAuthService implements AuthService {
  private tokenManager: TokenManager;

  constructor() {
    this.tokenManager = TokenManager.getInstance();
  }

  /**
   * Initiate OAuth 2.0 PKCE login flow (mobile-optimized)
   */
  public async login(): Promise<void> {
    try {
      // Check if mock auth is enabled
      if (import.meta.env.VITE_USE_MOCK_AUTH === 'true') {
        console.log('🔐 Mock auth enabled - simulating login');
        // Clear logout flag
        sessionStorage.removeItem('mock_logged_out');
        // Redirect to dashboard
        window.location.href = '/dashboard';
        return;
      }
      
      // Check if offline
      if (this.isOffline()) {
        throw this.tokenManager.createAuthError(
          AuthError.OFFLINE_MODE,
          'Cannot login while offline'
        );
      }

      // Generate PKCE parameters
      const codeVerifier = this.tokenManager.generateCodeVerifier();
      const codeChallenge = await this.tokenManager.generateCodeChallenge(codeVerifier);
      const state = this.tokenManager.generateState();

      // Store PKCE parameters
      this.tokenManager.storePKCEParams(codeVerifier, state);

      // Build authorization URL with mobile-optimized parameters
      const authUrl = this.buildAuthorizationUrl(codeChallenge, state);

      // For mobile, handle OAuth redirect more gracefully
      if (this.isMobileEnvironment()) {
        // Open in same window for better mobile UX
        window.location.href = authUrl;
      } else {
        // Fallback for web browsers
        window.location.href = authUrl;
      }
    } catch (error) {
      console.error('Mobile login failed:', error);
      throw error instanceof Error && 'type' in error 
        ? error 
        : this.tokenManager.createAuthError(
            AuthError.OAUTH_ERROR,
            'Failed to initiate login',
            error
          );
    }
  }

  /**
   * Handle OAuth callback (mobile-optimized)
   */
  public async handleCallback(urlParams: URLSearchParams): Promise<JWTUser> {
    try {
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      const error = urlParams.get('error');

      // Check for OAuth errors
      if (error) {
        const errorDescription = urlParams.get('error_description') || 'Unknown OAuth error';
        throw this.tokenManager.createAuthError(
          AuthError.OAUTH_ERROR,
          `OAuth error: ${error} - ${errorDescription}`
        );
      }

      if (!code || !state) {
        throw this.tokenManager.createAuthError(
          AuthError.OAUTH_ERROR,
          'Missing authorization code or state parameter'
        );
      }

      // Validate state parameter
      if (!this.tokenManager.validateState(state)) {
        throw this.tokenManager.createAuthError(
          AuthError.INVALID_STATE,
          'Invalid state parameter - possible CSRF attack'
        );
      }

      // Retrieve PKCE parameters
      const pkceParams = this.tokenManager.getPKCEParams();
      if (!pkceParams) {
        throw this.tokenManager.createAuthError(
          AuthError.OAUTH_ERROR,
          'Missing PKCE parameters'
        );
      }

      // Exchange code for tokens with mobile timeout
      const tokens = await this.exchangeCodeForTokens(code, pkceParams.codeVerifier);

      // Store tokens
      this.tokenManager.storeTokens(tokens);

      // Decode and store user
      const user = this.tokenManager.decodeIdToken(tokens.idToken);
      if (!user) {
        throw this.tokenManager.createAuthError(
          AuthError.OAUTH_ERROR,
          'Failed to decode user information'
        );
      }

      this.tokenManager.storeUser(user);

      return user;
    } catch (error) {
      console.error('Mobile callback handling failed:', error);
      throw error instanceof Error && 'type' in error 
        ? error 
        : this.tokenManager.createAuthError(
            AuthError.OAUTH_ERROR,
            'Failed to handle authentication callback',
            error
          );
    }
  }

  /**
   * Logout user (mobile-optimized)
   */
  public async logout(): Promise<void> {
    try {
      // Check if mock auth is enabled
      if (import.meta.env.VITE_USE_MOCK_AUTH === 'true') {
        console.log('🔐 Mock logout - clearing local state');
        this.tokenManager.clearTokens();
        // Set logout flag to prevent auto-login
        sessionStorage.setItem('mock_logged_out', 'true');
        // Redirect to login page
        window.location.href = '/login';
        return;
      }
      
      const tokens = this.tokenManager.getTokens();
      
      // Clear local data first
      this.tokenManager.clearTokens();

      // If online and have tokens, logout from Cognito
      if (!this.isOffline() && tokens) {
        try {
          const logoutUrl = this.buildLogoutUrl();
          
          // For mobile, provide fallback if redirect fails
          const timeoutId = setTimeout(() => {
            window.location.href = '/';
          }, 5000); // 5 second timeout
          
          window.location.href = logoutUrl;
          clearTimeout(timeoutId);
        } catch (networkError) {
          console.warn('Network logout failed, but local data cleared:', networkError);
          window.location.href = '/';
        }
      } else {
        // Offline or no tokens - just redirect
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Mobile logout failed:', error);
      // Always clear local data and redirect
      this.tokenManager.clearTokens();
      window.location.href = '/';
    }
  }

  /**
   * Get current user (mobile-optimized with offline support)
   */
  public async getCurrentUser(): Promise<JWTUser | null> {
    try {
      // Check if mock auth is enabled
      if (import.meta.env.VITE_USE_MOCK_AUTH === 'true') {
        return {
          sub: 'mock_rider_123',
          email: 'testrider@example.com',
          email_verified: true,
          'custom:user_type': 'individual' as const,
          'cognito:groups': [],
          given_name: 'Test',
          family_name: 'Rider',
          phone_number: '+1234567890',
          'custom:full_name': 'Test Rider'
        };
      }
      
      // In offline mode, use cached user data
      if (this.isOffline()) {
        return this.tokenManager.getUser();
      }

      // Online mode - check authentication
      if (!this.isAuthenticated()) {
        const hasValidTokens = await this.ensureValidTokens();
        if (!hasValidTokens) {
          return null;
        }
      }

      let user = this.tokenManager.getUser();
      
      if (!user) {
        const tokens = this.tokenManager.getTokens();
        if (tokens) {
          user = this.tokenManager.decodeIdToken(tokens.idToken);
          if (user) {
            this.tokenManager.storeUser(user);
          }
        }
      }

      return user;
    } catch (error) {
      console.error('Failed to get current mobile user:', error);
      return null;
    }
  }

  /**
   * Refresh tokens (mobile-optimized)
   */
  public async refreshTokens(): Promise<AuthTokens> {
    try {
      // Check if mock auth is enabled
      if (import.meta.env.VITE_USE_MOCK_AUTH === 'true') {
        const mockTokens: AuthTokens = {
          accessToken: 'mock_token_rider',
          refreshToken: 'mock_refresh_token',
          idToken: 'mock_id_token',
          tokenType: 'Bearer',
          expiresIn: 3600,
          expiresAt: Date.now() + 3600000
        };
        return mockTokens;
      }
      
      // Don't refresh in offline mode
      if (this.isOffline()) {
        throw this.tokenManager.createAuthError(
          AuthError.OFFLINE_MODE,
          'Cannot refresh tokens while offline'
        );
      }

      // Check for existing refresh promise
      const existingPromise = this.tokenManager.getRefreshPromise();
      if (existingPromise) {
        return existingPromise;
      }

      const currentTokens = this.tokenManager.getTokens();
      if (!currentTokens || !currentTokens.refreshToken) {
        throw this.tokenManager.createAuthError(
          AuthError.REFRESH_FAILED,
          'No refresh token available'
        );
      }

      // Create refresh promise with mobile timeout
      const refreshPromise = this.performTokenRefresh(currentTokens.refreshToken);
      this.tokenManager.setRefreshPromise(refreshPromise);

      const newTokens = await refreshPromise;
      this.tokenManager.storeTokens(newTokens);

      return newTokens;
    } catch (error) {
      console.error('Mobile token refresh failed:', error);
      
      // Clear tokens on refresh failure
      this.tokenManager.clearTokens();
      
      throw error instanceof Error && 'type' in error 
        ? error 
        : this.tokenManager.createAuthError(
            AuthError.REFRESH_FAILED,
            'Failed to refresh tokens',
            error
          );
    }
  }

  /**
   * Check authentication status (mobile-optimized)
   */
  public isAuthenticated(): boolean {
    // Check if mock auth is enabled
    if (import.meta.env.VITE_USE_MOCK_AUTH === 'true') {
      return true;
    }
    
    const tokens = this.tokenManager.getTokens();
    if (!tokens) {
      return false;
    }

    // In offline mode, allow longer grace period
    return this.tokenManager.isTokenValid();
  }

  /**
   * Check if in offline mode
   */
  public isOffline(): boolean {
    return this.tokenManager.isOffline();
  }

  /**
   * Ensure valid tokens (mobile-optimized)
   */
  public async ensureValidTokens(): Promise<boolean> {
    try {
      if (import.meta.env.VITE_USE_MOCK_AUTH === 'true') {
        return true;
      }

      const tokens = this.tokenManager.getTokens();
      if (!tokens) {
        return false;
      }

      if (this.isOffline()) {
        return this.tokenManager.getUser() !== null;
      }

      if (!this.tokenManager.isTokenValid() || this.tokenManager.needsRefresh()) {
        await this.refreshTokens();
      }

      return this.tokenManager.isTokenValid();
    } catch (error) {
      console.error('Failed to ensure valid mobile tokens:', error);
      return false;
    }
  }

  /**
   * Biometric authentication (future implementation hooks)
   */
  public async isBiometricAvailable(): Promise<boolean> {
    return await this.tokenManager.isBiometricEnabled();
  }

  public async enableBiometric(): Promise<boolean> {
    return await this.tokenManager.enableBiometric();
  }

  /**
   * Device memory management
   */
  public setRememberDevice(remember: boolean): void {
    this.tokenManager.setRememberDevice(remember);
  }

  public shouldRememberDevice(): boolean {
    return this.tokenManager.shouldRememberDevice();
  }

  /**
   * Check if running in mobile environment
   */
  private isMobileEnvironment(): boolean {
    return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  /**
   * Build authorization URL (mobile-optimized)
   */
  private buildAuthorizationUrl(codeChallenge: string, state: string): string {
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: authConfig.clientId,
      redirect_uri: authConfig.redirectUri,
      scope: authConfig.scopes.join(' '),
      state: state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      // Mobile-specific parameters
      response_mode: 'query', // Better for mobile
    });

    return `${authEndpoints.authorize}?${params.toString()}`;
  }

  /**
   * Build logout URL (mobile-optimized)
   */
  private buildLogoutUrl(): string {
    // Get the base URL (without /callback path)
    // Cognito logout endpoint uses 'logout_uri' parameter, not 'redirect_uri'
    const baseRedirectUri = authConfig.redirectUri.replace('/callback', '');
    const logoutUri = `${baseRedirectUri}/logout`;

    const params = new URLSearchParams({
      client_id: authConfig.clientId,
      logout_uri: logoutUri,
    });

    return `${authEndpoints.logout}?${params.toString()}`;
  }

  /**
   * Exchange code for tokens (mobile-optimized with timeout)
   */
  private async exchangeCodeForTokens(code: string, codeVerifier: string): Promise<AuthTokens> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), mobileAuthSettings.oauthTimeout);

    try {
      const response = await fetch(authEndpoints.token, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: authConfig.clientId,
          code: code,
          redirect_uri: authConfig.redirectUri,
          code_verifier: codeVerifier,
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Token exchange failed: ${response.status} - ${errorText}`);
      }

      const tokenData = await response.json();

      return {
        accessToken: tokenData.access_token,
        idToken: tokenData.id_token,
        refreshToken: tokenData.refresh_token,
        tokenType: tokenData.token_type,
        expiresIn: tokenData.expires_in,
        expiresAt: Date.now() + (tokenData.expires_in * 1000),
      };
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw this.tokenManager.createAuthError(
          AuthError.NETWORK_ERROR,
          'Token exchange timed out'
        );
      }
      throw error;
    }
  }

  /**
   * Perform token refresh (mobile-optimized with timeout)
   */
  private async performTokenRefresh(refreshToken: string): Promise<AuthTokens> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), mobileAuthSettings.oauthTimeout);

    try {
      const response = await fetch(authEndpoints.token, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: authConfig.clientId,
          refresh_token: refreshToken,
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Token refresh failed: ${response.status} - ${errorText}`);
      }

      const tokenData = await response.json();

      return {
        accessToken: tokenData.access_token,
        idToken: tokenData.id_token,
        refreshToken: tokenData.refresh_token || refreshToken,
        tokenType: tokenData.token_type,
        expiresIn: tokenData.expires_in,
        expiresAt: Date.now() + (tokenData.expires_in * 1000),
      };
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw this.tokenManager.createAuthError(
          AuthError.NETWORK_ERROR,
          'Token refresh timed out'
        );
      }
      throw error;
    }
  }
}

// Export singleton instance
export const authService = new CognitoAuthService();
